import moment from 'moment/moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Image } from './Image'
import { Option } from './Option'
import { RecordDataMap } from './RecordDataMap'
import { FKType } from './SQLBuilder'
import { Source } from './Source'

type TCRConstructorArgs = ConstructorArgs<() => TCR>

export class TCR extends BaseRecord {
    readonly recordType = 'tcr'

    state?: string
    adminState?: string
    firstName?: string
    lastName?: string
    personalStreet?: string
    personalCity?: string
    personalState?: string
    personalPostalCode?: string
    companyName?: string
    ein?: string
    entityType?: string
    companyStreet?: string
    companyCity?: string
    companyState?: string
    companyPostalCode?: string
    email?: string
    website?: string
    mobilePhone?: string
    photo?: Image
    selfie?: Image
    brandType?: string
    otpLastSentDate?: moment.Moment // Read-only
    areaCodePref?: string
    areaCodeAlt?: string
    portInRequest?: boolean // Boolean
    readonly portIn: boolean = false

    constructor(data?: Partial<TCRConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.state = data.state ?? undefined
        this.adminState = data.admin_state ?? undefined
        this.firstName = data.first_name ?? undefined
        this.lastName = data.last_name ?? undefined
        this.personalStreet = data.personal_street ?? undefined
        this.personalCity = data.personal_city ?? undefined
        this.personalState = data.personal_state ?? undefined
        this.personalPostalCode = data.personal_postal_code ?? undefined
        this.companyName = data.company_name ?? undefined
        this.ein = data.ein ?? undefined
        this.entityType = data.entity_type ?? undefined
        this.companyStreet = data.company_street ?? undefined
        this.companyCity = data.company_city ?? undefined
        this.companyState = data.company_state ?? undefined
        this.companyPostalCode = data.company_postal_code ?? undefined
        this.email = data.email ?? undefined
        this.website = data.website ?? undefined
        this.mobilePhone = data.mobile_phone ?? undefined
        this.photo = this.propertyFromImageArg(data.photo, data.photo_uri)
        this.selfie = this.propertyFromImageArg(data.selfie, data.selfie_uri)
        this.brandType = data.brand_type ?? undefined
        this.otpLastSentDate = this.propertyFromTimestampArg(data.otp_last_sent_date)
        this.areaCodePref = data.area_code_pref ?? undefined
        this.areaCodeAlt = data.area_code_alt ?? undefined
        this.portInRequest = data.port_in ?? false
        this.portIn = data.port_in ?? false
    }

    clone(overrides?: Partial<TCRConstructorArgs>): TCR {
        return new TCR({ ...this.serializeForClone(), ...overrides })
    }

    get isVisible(): boolean {
        if (this.state === undefined) return true
        if (this.state === 'done') {
            if (this.brandType !== undefined && this.brandType === 'SOLE_PROPRIETOR') {
                return (
                    this.adminState !== undefined &&
                    this.adminState === 'ad_done' &&
                    this.otpLastSentDate !== undefined
                )
            }
            return false
        }
        return true
    }

    static entityTypeOptions(): Option[] {
        let options: Option[] = []
        options.push(new Option(undefined, { id: 'partnership', label: 'Partnership' }))
        options.push(new Option(undefined, { id: 'sole_proprietor', label: 'Sole proprietor' }))
        options.push(new Option(undefined, { id: 'llc', label: 'LLC' }))
        options.push(new Option(undefined, { id: 'coop', label: 'Coop' }))
        options.push(new Option(undefined, { id: 'non_profit', label: 'Non-profit' }))
        options.push(new Option(undefined, { id: 'corporation', label: 'Corporation' }))
        return options
    }

    static StateNone = 'none'
    static StateAreaCode = 'areacode'
    static StatePersonalDetails = 'p_details'
    static StatePersonalAddress = 'p_address'
    static StateCompanyDetails = 'c_details'
    static StateCompanyAddress = 'c_address'
    static StateCompanyOther = 'c_other'
    static StatePhoto = 'photo'
    static StateSelfie = 'selfie'
    static StateDone = 'done'

    deserialize(data: any) {
        super.deserialize(data)

        if (data.photo) this.photo = BaseRecord.fromSerializer(data.photo, Image)
        if (data.selfie) this.selfie = BaseRecord.fromSerializer(data.selfie, Image)
        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.otpLastSentDate) this.otpLastSentDate = moment(data.otpLastSentDate)
    }

    static fk: FKType = {
        images: ['photo', 'selfie'],
        sources: ['source'],
    }

    static dataType(): DataType {
        return 'tcrs'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['first_name'] = this.firstName
        dict['last_name'] = this.lastName
        dict['personal_street'] = this.personalStreet
        dict['personal_city'] = this.personalCity
        dict['personal_state'] = this.personalState
        dict['personal_postal_code'] = this.personalPostalCode
        dict['company_name'] = this.companyName
        dict['ein'] = this.ein
        dict['entity_type'] = this.entityType
        dict['company_street'] = this.companyStreet
        dict['company_city'] = this.companyCity
        dict['company_state'] = this.companyState
        dict['company_postal_code'] = this.companyPostalCode
        dict['email'] = this.email
        dict['website'] = this.website
        dict['mobile_phone'] = this.mobilePhone
        dict['photo'] = this.photo?.id ?? ''
        dict['selfie'] = this.selfie?.id ?? ''
        if (this.source) dict['source'] = this.source.id
        dict['brand_type'] = this.brandType
        dict['area_code_pref'] = this.areaCodePref
        dict['area_code_alt'] = this.areaCodeAlt
        dict['port_in'] = this.portIn
        return dict
    }

    get source(): Source | undefined {
        return this.getSourceRecord('source')
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
}
